import { useState } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Clock, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { schedulerApi, type TriggerCreateInput } from '@/lib/scheduler-api';
import { CreateTriggerWizard } from './CreateTriggerWizard';

interface TriggerCreateDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

export function TriggerCreateDrawer({ open, onOpenChange, onSuccess }: TriggerCreateDrawerProps) {
    const [saving, setSaving] = useState(false);

    const handleClose = () => {
        if (saving) return;
        onOpenChange(false);
    };

    const handleSave = async (input: TriggerCreateInput) => {
        setSaving(true);
        try {
            await schedulerApi.createTrigger(input);
            toast.success('Trigger created.');
            onOpenChange(false);
            onSuccess?.();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to create trigger.';
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <SlideDrawer isOpen={open} onClose={handleClose}>
                        <div className="relative border-b border-slate-100 bg-slate-50/50 px-8 py-6">
                            <div className="mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-100">
                                        <Clock className="h-5 w-5 text-sky-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-semibold text-slate-900">
                                            Add New Trigger
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            Create a scheduled trigger to automate capability invocations.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={saving}
                                    className="mr-1 rounded-full p-2 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900 disabled:opacity-50"
                                    aria-label="Close"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto bg-white px-12 py-8">
                            <div className="w-full">
                                <CreateTriggerWizard onSave={handleSave} saving={saving} />
                            </div>
                        </div>

                        {saving && (
                            <div className="flex items-center justify-end border-t border-slate-100 bg-slate-50/60 px-8 py-3 text-sm text-slate-500">
                                <Loader2 size={14} className="mr-2 animate-spin" />
                                Saving trigger...
                            </div>
                        )}
        </SlideDrawer>
    );
}
